/**
 * Documentation citations sent by the AI assistant (kb-agent via
 * analytics-agent, relayed by the chat API as a `citations` SSE event).
 *
 * Field names follow kb-agent's contract (aiagents/kb-agent/README.md). The
 * media URLs are signed KI RAG content URLs: they need no token and expire
 * after ~15 minutes, so they are only valid for the live answer.
 */

export interface CitationBBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface CitationDisplay {
  page_number?: number | null;
  page_count?: number | null;
  bbox?: CitationBBox | null;
  bbox_space?: string | null;
  start_ms?: number | null;
  end_ms?: number | null;
  label?: string | null;
}

export interface CitationMedia {
  kind: string;
  url: string;
  content_type?: string;
  url_expires_at?: string;
}

export interface Citation {
  index: number;
  excerpt: string;
  source: string;
  chunk_id?: string;
  document?: { document_id?: string; filename?: string; modality?: string };
  scope?: string;
  /** Knowledge-base id of a global or shared hit (null for a tenant hit). */
  kb_id?: string | null;
  score?: number;
  /** KI RAG passage source; "SUMMARY" is the Media Summary of a whole recording. */
  content_source?: string | null;
  locator?: unknown;
  display?: CitationDisplay | null;
  media?: CitationMedia | null;
  document_url?: string;
  document_url_expires_at?: string;
  play_url?: string;
}

const IMAGE_KINDS = new Set(['page_image', 'keyframe', 'embedded_image', 'image', 'thumbnail']);

/** Only absolute https URLs are rendered (never javascript:, data:, relative). */
export function safeUrl(url: unknown): string | undefined {
  if (typeof url !== 'string' || !url) return undefined;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' ? u.toString() : undefined;
  } catch {
    return undefined;
  }
}

/** The image to preview for a citation (page image, keyframe, picture). */
export function imageUrl(c: Citation): string | undefined {
  if (!c.media || !IMAGE_KINDS.has(c.media.kind)) return undefined;
  return safeUrl(c.media.url);
}

/** True when the citation points into a video or audio recording. */
export function isMediaClip(c: Citation): 'video' | 'audio' | undefined {
  const modality = (c.document?.modality ?? '').toUpperCase();
  const audio = modality === 'AUDIO' || c.media?.kind === 'audio';
  const recording = audio || modality === 'VIDEO' || c.media?.kind === 'video';
  if (!safeUrl(c.play_url) && !(recording && safeUrl(c.document_url))) return undefined;
  return audio ? 'audio' : 'video';
}
/** True for a KI RAG Media Summary hit (content_source SUMMARY, any case). */
export function isSummary(c: Citation): boolean {
  return String(c.content_source ?? '').toUpperCase() === 'SUMMARY';
}
/** The <video>/<audio> source: play_url (carries #t=start,end), else the original file. */
export function clipUrl(c: Citation): string | undefined {
  return safeUrl(c.play_url) ?? safeUrl(c.document_url);
}
/**
 * The cited scene in seconds, for seeking on loadedmetadata and pausing once at
 * the end. Missing or invalid values are undefined.
 */
export function clipRange(c: Citation): { start?: number; end?: number } {
  // A summary covers the whole recording: play from the start, no pause.
  if (isSummary(c)) return { start: 0, end: undefined };
  const sec = (ms?: number | null) =>
    typeof ms === 'number' && Number.isFinite(ms) && ms >= 0 ? ms / 1000 : undefined;
  const start = sec(c.display?.start_ms);
  const end = sec(c.display?.end_ms);
  return { start, end: end !== undefined && (start === undefined || end > start) ? end : undefined };
}
/** True when an ISO timestamp (url_expires_at, document_url_expires_at) is in the past. */
export function isExpired(expiresAt?: string | null, now: number = Date.now()): boolean {
  if (!expiresAt) return false;
  const t = Date.parse(expiresAt);
  return Number.isFinite(t) && t <= now;
}
/** The PDF page cited, when the citation is a PDF page with a page number. */
export function pdfPage(c: Citation): number | undefined {
  const n = c.display?.page_number;
  const modality = (c.document?.modality ?? '').toUpperCase();
  const pdf = modality === 'PDF' || /\.pdf$/i.test(c.document?.filename ?? '');
  return pdf && typeof n === 'number' && Number.isInteger(n) && n > 0 ? n : undefined;
}
/**
 * The link that opens the original document in a new tab: for a PDF page
 * `document_url#page=N` (the browser PDF viewer jumps to the page).
 */
export function documentLink(c: Citation): string | undefined {
  const url = safeUrl(c.document_url);
  const page = pdfPage(c);
  if (!url || page === undefined) return url;
  const u = new URL(url);
  u.hash = `page=${page}`;
  return u.toString();
}

/**
 * The bbox as CSS percentages over the image, or undefined when it is missing
 * or not a valid 0..1 fraction box (origin top-left).
 */
export function bboxStyle(bbox?: CitationBBox | null): Record<string, string> | undefined {
  if (!bbox) return undefined;
  const { x, y, w, h } = bbox;
  const ok = [x, y, w, h].every((v) => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1);
  if (!ok || w <= 0 || h <= 0 || x + w > 1.0001 || y + h > 1.0001) return undefined;
  const pct = (v: number) => `${(v * 100).toFixed(2)}%`;
  return { left: pct(x), top: pct(y), width: pct(w), height: pct(h) };
}

/** Short position label: "Page 12 of 140", "0:00:42.000 - 0:00:48.500", ... */
export function positionLabel(c: Citation): string {
  if (c.display?.label) return c.display.label;
  const name = c.document?.filename;
  if (name && c.source.startsWith(name)) {
    return c.source.slice(name.length).trim().replace(/^\((.*)\)$/, '$1');
  }
  return '';
}

/** Display name of the cited document. */
export function documentName(c: Citation): string {
  return c.document?.filename || c.source;
}

/**
 * Merges citation lists from several documentation calls of one answer, in
 * order, without duplicates (same chunk or same source).
 */
export function mergeCitations(existing: Citation[], incoming: unknown): Citation[] {
  if (!Array.isArray(incoming)) return existing;
  const seen = new Set(existing.map(citationKey));
  const out = [...existing];
  for (const raw of incoming) {
    if (!raw || typeof raw !== 'object') continue;
    const c = raw as Citation;
    if (typeof c.source !== 'string') continue;
    const key = citationKey(c);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(c);
  }
  return out;
}

/** Stable identity of a citation within one answer (chunk, else source + position). */
export function citationKey(c: Citation): string {
  return c.chunk_id || `${c.source}|${c.display?.start_ms ?? ''}|${c.display?.page_number ?? ''}`;
}

// ─── Subtitles of a cited video (read from KI RAG, never requested) ──────────
// Mirrors the KI RAG web app (platform/web/src/lib/subtitles.js subtitleOffer
// and App.jsx VideoPlayer): the translations list says which languages exist,
// the detail of a request carries the signed WebVTT URL.

/** One item of GET {prefix}/documents/{id}/translations. */
export interface TranslationItem {
  translation_id: string;
  target_language: string;
  job_state?: string;
  approval_state?: string;
  current?: boolean;
  outdated?: boolean;
  error_code?: string;
}

/** One artifacts[] entry of GET {prefix}/documents/{id}/translations/{tid}. */
export interface TranslationArtifact {
  artifact_kind?: string;
  output?: string;
  url?: string;
  url_expires_at?: string;
}

/** Same done states as KI RAG's isDoneState. */
function isDoneState(state?: string): boolean {
  return ['DONE', 'SUCCEEDED', 'SUCCEEDED_WITH_WARNINGS'].includes(String(state ?? '').toUpperCase());
}

function validItems(items: unknown): TranslationItem[] {
  return (Array.isArray(items) ? items : []).filter(
    (r): r is TranslationItem =>
      !!r && typeof r === 'object' && typeof r.translation_id === 'string' && typeof r.target_language === 'string',
  );
}

function hasNoSpeech(list: TranslationItem[], hasSpeech: boolean): boolean {
  return (
    !hasSpeech ||
    list.some((r) => !r.outdated && r.job_state === 'FAILED' && r.error_code === 'ERR_INVALID_REQUEST')
  );
}

/**
 * The requests whose subtitles the player offers (KI RAG subtitleOffer
 * `current`, 43.33/43.38/43.39): nothing when the video has no speech; else
 * one current, non-outdated, done request per language (the list is newest
 * first, so the first one wins). Outdated tracks are never played.
 */
export function currentSubtitleRequests(items: unknown, hasSpeech: boolean): TranslationItem[] {
  const list = validItems(items);
  if (hasNoSpeech(list, hasSpeech)) return [];
  const seen = new Set<string>();
  const out: TranslationItem[] = [];
  for (const r of list) {
    if (!r.current || r.outdated || !isDoneState(r.job_state) || seen.has(r.target_language)) continue;
    seen.add(r.target_language);
    out.push(r);
  }
  return out;
}

/** What the translations list offers the player for this version of the video. */
export type SubtitleListState = 'current' | 'outdated' | 'none' | 'no-speech';

/**
 * current: at least one playable request; outdated: requests exist but none
 * is current for this version (e.g. after a KI RAG reprocess); none: no
 * request at all; no-speech: same rule as currentSubtitleRequests.
 */
export function subtitleListState(items: unknown, hasSpeech: boolean): SubtitleListState {
  const list = validItems(items);
  if (hasNoSpeech(list, hasSpeech)) return 'no-speech';
  if (currentSubtitleRequests(list, hasSpeech).length > 0) return 'current';
  if (list.some((r) => r.outdated || !r.current)) return 'outdated';
  return 'none';
}

/** The signed WebVTT URL of a translation detail (output "subtitles"), https only. */
export function subtitleArtifactUrl(artifacts: unknown): string | undefined {
  if (!Array.isArray(artifacts)) return undefined;
  const hit = (artifacts as TranslationArtifact[]).find((a) => a && a.output === 'subtitles');
  return safeUrl(hit?.url);
}

/**
 * The KI RAG path prefix of the cited document (KI RAG web app kbBase):
 * global → /v1/global, shared with a kb_id → /v1/shared/{kb_id}, otherwise the
 * caller's own org from the token (never from citation data).
 */
export function kiragPrefix(c: Citation, org?: string | null): string | undefined {
  if (c.scope === 'global') return '/v1/global';
  if (c.scope === 'shared' && typeof c.kb_id === 'string' && c.kb_id) {
    return `/v1/shared/${encodeURIComponent(c.kb_id)}`;
  }
  return org ? `/v1/tenants/${encodeURIComponent(org)}` : undefined;
}

/**
 * The language whose track is on by default: the UI language exactly, else
 * the same primary subtag (de-DE → de), else none (the original speech plays
 * without subtitles; every track stays in the player's menu).
 */
export function pickDefaultSubtitle(languages: string[], uiLanguage?: string | null): string | undefined {
  if (!uiLanguage) return undefined;
  const ui = uiLanguage.toLowerCase();
  const exact = languages.find((l) => l.toLowerCase() === ui);
  if (exact) return exact;
  const primary = (tag: string) => tag.toLowerCase().split('-')[0];
  return languages.find((l) => primary(l) === primary(ui));
}

// The language names of the KI RAG subtitle picker (App.jsx SUBTITLE_LANGUAGES).
const SUBTITLE_LANGUAGE_NAMES: Record<string, string> = {
  de: 'Deutsch',
  en: 'English',
  fr: 'Français',
  es: 'Español',
  it: 'Italiano',
  pl: 'Polski',
  'zh-CN': '中文',
  tr: 'Türkçe',
  nl: 'Nederlands',
  cs: 'Čeština',
};

/** Display name of a subtitle language, the tag itself when unknown. */
export function subtitleLanguageName(tag: string): string {
  return SUBTITLE_LANGUAGE_NAMES[tag] ?? tag;
}
